import moment from 'moment';
import {CarePlanConfigurationFeature} from '../../backend/enums/care-plan-configuration-feature';
import {CarePlanConfigurationType} from '../../backend/enums/care-plan-configuration-type';
import {CarePlanConfiguration} from '../../backend/interfaces/care-plan-configuration';
import {CarePlanConfigurationLayout} from '../../backend/interfaces/care-plan-configuration-layout';
import {ConfigurationsValuesIndex} from '../../types';
import {convertArrayToIndex} from '../../utils/functions';
import {Option} from '../flat-select-dropdown/flat-select-dropdown';
import {ConfigurationsIndex} from './../../types/configurations';

/**
 *
 * Handles the component first render value as a controlled field.
 *
 * */
export const getFieldInitialControlledRenderValue = (field: CarePlanConfiguration) => {
  // Always return '' (empty string) for input fields to avoid React null value warning
  if (
    field.type === CarePlanConfigurationType.String ||
    field.type === CarePlanConfigurationType.Number ||
    field.type === CarePlanConfigurationType.Integer ||
    field.type === CarePlanConfigurationType.Autocomplete ||
    field.type === CarePlanConfigurationType.Boolean
  ) {
    return '';
  }

  // Fallback: return '' instead of null for any other type
  return '';
};

/**
 *
 * Maps the controlled pre-filled form values
 *
 * */
export const mapPrefilledValues = (
  configurationsIndex: ConfigurationsIndex,
  defaultValuesIndex: ConfigurationsValuesIndex
) => {
  let VALUES: {id: string; value: any}[] = [];

  defaultValuesIndex &&
    Object.entries(defaultValuesIndex).map(([key, value]) => {
      if (value.carePlanConfigurationValue === null) return undefined;

      let defaultValue: any = undefined;

      if (!configurationsIndex) return undefined;

      const field = configurationsIndex[key];

      // If the field is not found, most probaly the value is not needed anymore.
      if (!field) return undefined;

      switch (field.type) {
        case CarePlanConfigurationType.Array:
          if (!field.arrayItems) return undefined;

          const indexedArrayItems: any = convertArrayToIndex(field.arrayItems, 'id');

          if (field.multipleValues) {
            if (value.carePlanConfigurationValue) {
              defaultValue = value.carePlanConfigurationValue
                ?.map((item: string | Option) => {
                  /**
                   * This logic is here as a failsafe to account for old data that was wrongly stored as an object instead of `id[]`.
                   */
                  // @ts-ignore
                  let object = indexedArrayItems[item?.key] ?? indexedArrayItems[item as string];

                  if (!object) return undefined;

                  let option: Option = {
                    key: object?.id ?? object.name,
                    label: object.name,
                  };

                  if (field?.feature) {
                    if (field.feature === CarePlanConfigurationFeature.AutonomyAssessment) {
                      defaultValue.selectedColor = object.color;
                    }
                  }

                  return option;
                })
                .filter((item: string | Option) => item !== undefined);
            } else {
              defaultValue = null;
            }
          } else {
            if (indexedArrayItems[value.carePlanConfigurationValue]) {
              defaultValue = {
                key:
                  indexedArrayItems[value.carePlanConfigurationValue]?.id ??
                  indexedArrayItems[value.carePlanConfigurationValue]?.name,
                label: indexedArrayItems[value.carePlanConfigurationValue]?.name,
              };

              if (field?.feature) {
                if (field.feature === CarePlanConfigurationFeature.AutonomyAssessment) {
                  defaultValue.selectedColor =
                    indexedArrayItems[value.carePlanConfigurationValue]?.color;
                }
              }
            } else {
              defaultValue = null;
            }
          }

          break;

        case CarePlanConfigurationType.Date:
        case CarePlanConfigurationType.Time:
          defaultValue = value.carePlanConfigurationValue || moment().toISOString();
          break;

        default:
          defaultValue = value.carePlanConfigurationValue;
          break;
      }

      if (field.id) VALUES.push({id: field.id, value: defaultValue});

      return undefined;
    });

  return reduceFormValues(VALUES);
};

/**
 *
 * Maps the controlled default values.
 *
 * */
export const mapDefaultValues = (
  configurationsIndex: ConfigurationsIndex,
  layout?: CarePlanConfigurationLayout
) => {
  let DEFAULTS: {id: string; value: any}[] = [];

  for (const configuration in configurationsIndex) {
    const field = configurationsIndex[configuration];

    let defaultValue: any = undefined;

    if (field?.feature === CarePlanConfigurationFeature.StartsAtTime) {
      // default value time is now
      const now = moment();
      // Round up to the next 5 minute interval, adding more 5 minutes if next interval is closer than 1 minute
      // eg: 12:00 -> 12:05 || 12:04 -> 12:10
      const minutesToAdd = 5 - (now.minutes() % 5) + (now.minutes() % 5 >= 4 ? 5 : 0);
      defaultValue = moment().add(minutesToAdd, 'minute').set({second: 0, millisecond: 0});
    }

    if (field?.feature === CarePlanConfigurationFeature.StartsAtDate) {
      // default value time is now
      defaultValue = moment().set({hour: 0, minute: 0, second: 0, millisecond: 0});
    }

    if (
      field?.feature === CarePlanConfigurationFeature.ActivationState ||
      field?.type === CarePlanConfigurationType.Boolean
    ) {
      if (layout && layout?.configurations) {
        for (const configItem of layout?.configurations) {
          if (configItem.carePlanConfigurationId === field.id) defaultValue = configItem.default;
        }
      }
    }

    if (field.id) DEFAULTS.push({id: field.id, value: defaultValue});
  }

  return reduceFormValues(DEFAULTS);
};

/**
 *
 * Reduces the values to a mapped object of [id]: value
 * */
export const reduceFormValues = (
  array: {
    id: string;
    value: any;
  }[]
) => {
  return array.reduce((acc, item: {id: string; value: string}) => {
    if (item.value !== null) {
      // @ts-ignore
      acc[item.id] = item.value;
    }
    return acc;
  }, {});
};
